import React, { useState, useEffect } from "react"
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  ListItem,
  ListItemText,
  Typography,
  InputAdornment,
  CircularProgress
} from "@mui/material"

import { makeStyles } from "tss-react/mui"

import { debounce } from "lodash"

import SearchIcon from "@mui/icons-material/Search"
import { Option } from "../../Types"

import { FixedSizeList } from "react-window"
import InfiniteLoader from "react-window-infinite-loader"
import AutoSizer from "react-virtualized-auto-sizer"
import { CloseIcon, DropdownIcon, RemoveIcon } from "../../../../icons"
import { blueGrey, lightBlue } from "../../../../themes/palette"

// const AutoSizer = CAutoSizer as any;

interface MobileSearchField {
  index: number
  label: string
  selected: Option
  options: [Option]
  cursor: any // querysnapshot last item
  onClear: () => void
  onChange: (option: Option) => void
  onYReachEnd: (text: string) => void
  onSearchTextChange: (text: string) => void
  loading: boolean
  disabled: boolean
  paginating: boolean
  Icon: React.ComponentType<React.PropsWithChildren<any>>
  disableActions?: boolean
}

const MobileSearchField: React.FC<
  React.PropsWithChildren<MobileSearchField>
> = ({
  label,
  index,
  selected,
  options,
  cursor,
  onSearchTextChange,
  onYReachEnd,
  onClear,
  onChange,
  disabled,
  loading,
  paginating,
  Icon,
  disableActions = false
}) => {
  const { classes, cx } = styles()

  const [dialog, setDialog] = useState(false)
  const [text, setText] = useState("")
  // to show loading on debounced searching event
  const [searching, setSearching] = useState(false)

  // reset search text when selection is cleared up
  useEffect(() => {
    if (!selected && Boolean(text)) {
      setText("")
      // also reset the options if it is first selector
      if (index === 0) {
        onSearchTextChange("")
      }
    }
  }, [selected, disabled])

  useEffect(() => {
    if (searching && !loading) {
      setSearching(false)
    }
  }, [loading])

  useEffect(() => {
    if (!disableActions) return

    setDialog(false)
  }, [disableActions])

  const renderLoader = (style?: { [key: string]: string | number }) => {
    return (
      <div style={style} className={classes.loader}>
        <CircularProgress size={32} color="secondary" />
      </div>
    )
  }

  // Infinite scroll required stuff start
  const isItemLoaded = (index: number) => {
    if (!Boolean(cursor) || index < options.length) return true
    return false
  }

  const Item = ({ index, style }: any) => {
    if (!isItemLoaded(index)) {
      return renderLoader(style)
    } else {
      const option = options[index]
      return (
        <ListItem
          button
          style={style}
          onClick={e => {
            setDialog(false)
            onChange(option)
          }}
          key={index}
          className={cx(
            classes.listItem,
            option?.value &&
              selected?.value == option?.value &&
              classes.selected
          )}
        >
          <ListItemText className={classes.listText} primary={option.label} />
          {selected?.value == option?.value && !disableActions && (
            <IconButton
              style={{
                margin: "0px 8px"
              }}
              size="small"
              onClick={e => {
                e.stopPropagation()
                onClear()
                onSearchTextChange("")
              }}
            >
              <RemoveIcon style={{ fontSize: "1rem", color: "#1693BE" }} />
            </IconButton>
          )}
        </ListItem>
      )
    }
  }
  // Infinite scroll required stuff end

  const debouncedSearch = debounce((text: any) => {
    onSearchTextChange(text)
  }, 500)

  const isFilterListEmpty = options.length < 1

  return (
    <>
      <TextField
        onKeyPress={e => {
          if (e.key === "Enter") {
            if (disableActions) return

            if (!disabled) {
              setDialog(true)
            }
          }
        }}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disableActions) return

          if (!disabled) {
            setDialog(true)
          }
        }}
        fullWidth={true}
        // label={item.label}
        value={selected?.label ?? ""}
        variant="filled"
        label={
          <>
            <Icon
              style={{ fontSize: "0.75rem", color: "#4DBBDF", marginRight: 8 }}
            />
            <span>{label} </span>
          </>
        }
        className={cx(Boolean(selected) && "active", disabled && "disabled")}
        classes={{
          root: classes.textFieldRoot
        }}
        inputProps={{
          readOnly: true
        }}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end">
              {!disableActions && (
                <Box>
                  {Boolean(selected) && (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        onClear()
                      }}
                      tabIndex={disabled ? -1 : 0}
                    >
                      <RemoveIcon
                        style={{
                          size: "1rem",
                          color: "rgba(255,255,255,0.5)"
                        }}
                      />
                    </IconButton>
                  )}

                  <IconButton
                    disabled={disabled}
                    size="small"
                    className={cx(
                      Boolean(selected) && classes.activeIcon,
                      disabled && classes.disabledIcon
                    )}
                    tabIndex={disabled ? -1 : 0}
                    onClick={e => {
                      e.stopPropagation()
                      setDialog(true)
                    }}
                  >
                    <DropdownIcon
                      style={{
                        size: "1rem"
                        // color: Boolean(selected) ? "#fff" : "#000"
                      }}
                    />
                  </IconButton>
                </Box>
              )}
            </InputAdornment>
          )
        }}
        InputLabelProps={{
          shrink: selected ? true : false,
          classes: {
            root: classes.label
          }
        }}
      />
      <Dialog open={dialog} keepMounted={false} fullScreen={true}>
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Box display="flex" alignItems="center" mb={3}>
            <IconButton
              style={{ marginRight: 8 }}
              onClick={() => setDialog(false)}
              size="large"
            >
              <CloseIcon style={{ fontSize: "1rem" }} />
            </IconButton>
            <Typography variant="h3">{label}</Typography>
          </Box>
          <TextField
            label=" "
            value={text}
            onChange={e => {
              setText(e.target.value)
              setSearching(true)
              debouncedSearch(e.target.value)
            }}
            autoFocus={true}
            variant="outlined"
            fullWidth={true}
            disabled={isFilterListEmpty && !text}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              notched: false
            }}
          />
        </DialogTitle>
        <DialogContent
          classes={{
            root: classes.dialogContentRoot
          }}
        >
          {loading || searching ? (
            renderLoader()
          ) : (
            <div style={{ height: "100%", width: "100%" }}>
              <AutoSizer>
                {({ height, width }) => {
                  return (
                    <InfiniteLoader
                      threshold={1}
                      isItemLoaded={isItemLoaded}
                      itemCount={
                        Boolean(cursor) ? options.length + 1 : options.length
                      }
                      loadMoreItems={(
                        startIndex: number,
                        stopIndex: number
                      ) => {
                        if (!(loading || searching || paginating))
                          onYReachEnd(text)
                      }}
                    >
                      {({ onItemsRendered, ref }) => (
                        <FixedSizeList
                          itemCount={
                            Boolean(cursor)
                              ? options.length + 1
                              : options.length
                          }
                          onItemsRendered={onItemsRendered}
                          ref={ref}
                          height={height}
                          width={"100%"}
                          itemSize={50}
                        >
                          {Item}
                        </FixedSizeList>
                      )}
                    </InfiniteLoader>
                  )
                }}
              </AutoSizer>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

const styles = makeStyles()(() => ({
  // filter text fields
  dialogContentRoot: {
    display: "flex",
    flexGrow: 1,
    padding: 0,

    overflowY: "hidden"
  },
  dialogTitleRoot: {},
  label: {
    display: "flex",
    alignItems: "center",
    "& > :first-of-type": {
      marginRight: 4
    }
  },
  selected: {
    paddingRight: 8,
    backgroundColor: "#E0F7FA"
  },
  textFieldRoot: {
    "& .MuiFilledInput-root": {
      backgroundColor: "#fff",
      border: `1px solid ${lightBlue[300]}`,
      borderRadius: 4
    },
    "& .MuiInputLabel-root": {
      display: "flex",
      alignItems: "center",
      "& > :first-of-type": {
        marginRight: 4,
        color: lightBlue[300]
      }
    },

    "&.active": {
      "& .MuiInputBase-input": {
        color: "#fff"
      },
      "& .MuiInputLabel-root span": {
        color: "#fff"
      }
    },
    "&.disabled": {
      "& .MuiFilledInput-root": {
        color: blueGrey[50],
        borderColor: blueGrey[100]
      },
      "& .MuiInputLabel-root": {
        "& > :first-of-type": {
          color: blueGrey[100]
        },
        "& > :last-child": {
          color: blueGrey[500]
        }
      }
    },

    "&.active:nth-last-of-type(5) .MuiFilledInput-root": {
      backgroundColor: "#0A526C",
      borderColor: "#0A526C"
    },
    "&.active:nth-last-of-type(4) .MuiFilledInput-root": {
      backgroundColor: "#0B5C7A",
      borderColor: "#0B5C7A"
    },
    "&.active:nth-last-of-type(3) .MuiFilledInput-root": {
      backgroundColor: "#0C6788",
      borderColor: "#0C6788"
    },
    "&.active:nth-last-of-type(2) .MuiFilledInput-root": {
      backgroundColor: "#0E7296",
      borderColor: "#0E7296"
    },
    "&.active:nth-last-of-type(1) .MuiFilledInput-root": {
      backgroundColor: lightBlue[600],
      borderColor: lightBlue[600]
    }
  },

  activeIcon: {
    color: "#fff"
  },
  disabledIcon: {
    color: blueGrey[200]
  },
  // listitem styles
  listItem: {
    overflow: "hidden",
    width: "100%",
    padding: 0
  },
  listText: {
    "& span": {
      fontSize: 14,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      paddingLeft: 24, // 12px
      paddingRight: 6
    }
  },
  // listitem styles end

  // loader start
  loader: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    overflow: "hidden",
    justifyContent: "flex-start",
    alignItems: "center"
  }
  // loader end
}))

export default MobileSearchField
